/* global console */

import { useEffect } from "react";

import { getUserId } from "src/auth/auth-client";
import { serverApi } from "src/api/server-api";
import { useAppDispatch } from "src/hooks";
import { initializeTelemetry, setUserContext } from "src/services/telemetry";

/**
 * Fetch the Application Insights connection string from the backend and start
 * the browser SDK.
 *
 * Unlike Sentry, whose DSN is baked in at build time, the connection string is
 * fetched at runtime — which is why this hook exists at all.
 *
 * Must be called from a component that only mounts once the user is signed in:
 * `/api/config/client` sits behind Okta auth, so an unauthenticated call 401s.
 * `TaskpaneContent` satisfies this, which is why there is no `authed` check here.
 *
 * Every failure path is silent. Telemetry is optional infrastructure — a missing
 * connection string or an unreachable endpoint must never stop someone from
 * styling a manuscript.
 */
export function useTelemetryInit(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const config = await dispatch(serverApi.endpoints.getClientConfig.initiate()).unwrap();

        // The backend returns `telemetryEnabled: true` with a null connection
        // string when it cannot resolve one, so both must be checked.
        if (cancelled || !config.telemetryEnabled || !config.applicationInsightsConnectionString) {
          return;
        }

        initializeTelemetry(config.applicationInsightsConnectionString);

        const userId = getUserId();
        if (userId) {
          setUserContext(userId);
        }
      } catch (err) {
        console.warn("[telemetry] init skipped:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);
}
